import ApiService from '../api'
import UserService from '../users/UserService'
import CommonService from '../CommonService'
import router from '../../router'

class AuthenticationService {
  constructor() {
    this.tokenKey = 'auth_token'
    this.userIdKey = 'user_id'
    this.jwtExpiryKey = 'jwt_expiry'
    this.userDataKey = 'user_data'
    this.userStoreKey = 'user_store_data'
  }

  // ==========================================
  // EXTERNAL / SSO LOGIN SUPPORT
  // ==========================================
  setToken(token) {
    const expiry = new Date()
    expiry.setHours(expiry.getHours() + 24) // 24h fallback expiry

    this.setJwt(token)
    this.setJWTExpiry(expiry.toISOString())
  }

  // ==========================================
  // JWT + SESSION HELPERS
  // ==========================================
  setJwt(token, isImpersonation = false) {
    if (isImpersonation) {
      localStorage.setItem('access_token', token)
      localStorage.setItem('token', token)
    } else {
      localStorage.setItem(this.tokenKey, token)
    }

    ApiService.setAuthToken(token)
  }

  getJwt() {
    const impersonationActive = localStorage.getItem('impersonation_active') === 'true'

    if (impersonationActive) {
      return localStorage.getItem('access_token') || localStorage.getItem('token')
    }

    return localStorage.getItem(this.tokenKey)
  }

  removeJwt() {
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem('access_token')
    localStorage.removeItem('token')
    ApiService.setAuthToken(null)
  }

  setJWTExpiry(expiry) {
    localStorage.setItem(this.jwtExpiryKey, expiry)
  }

  getJWTExpiry() {
    return localStorage.getItem(this.jwtExpiryKey)
  }

  removeJWTExpiry() {
    localStorage.removeItem(this.jwtExpiryKey)
  }

  // ==========================================
  // AUTH STATE
  // ==========================================
  isAuthenticated() {
    const impersonationActive = localStorage.getItem('impersonation_active') === 'true'

    if (impersonationActive) {
      const token = localStorage.getItem('access_token')
      return !!token && token.length > 10
    }

    const token = this.getJwt()
    const expiry = this.getJWTExpiry()

    if (!token || !expiry) return false

    const now = new Date()
    const expiryDate = new Date(expiry)

    if (now >= expiryDate) {
      this.logout()
      return false
    }

    return true
  }

  // ==========================================
  // USER DATA
  // ==========================================
  setUserData(userData) {
    if (userData) {
      localStorage.setItem(this.userDataKey, JSON.stringify(userData))
    } else {
      localStorage.removeItem(this.userDataKey)
    }
  }

  getUserData() {
    const impersonationActive = localStorage.getItem('impersonation_active') === 'true'

    if (impersonationActive) {
      const impersonated = localStorage.getItem('impersonated_user')
      return impersonated ? JSON.parse(impersonated) : null
    }

    const data = localStorage.getItem(this.userDataKey)
    return data ? JSON.parse(data) : null
  }

  setUserStoreData(data) {
    localStorage.setItem(this.userStoreKey, JSON.stringify(data))
  }

  getUserStoreData() {
    const data = localStorage.getItem(this.userStoreKey)
    return data ? JSON.parse(data) : {}
  }

  removeUserStoreData() {
    localStorage.removeItem(this.userStoreKey)
  }

  // ==========================================
  // LOGIN / LOGOUT
  // ==========================================
  async login(credentials) {
    const response = await ApiService.post('/login', {}, credentials)

    if (!response?.Success || response.Data.status !== 'success') {
      return { success: false, message: response?.Data?.message }
    }

    this.setJwt(response.Data.access_token)
    this.setJWTExpiry(response.Data.expires_at)

    const me = await UserService.getMe(response.Data.data.id)

    if (!me?.Success) {
      return { success: false }
    }

    this.setUserData(me.Data.data)

    const storeData = {
      userId: me.Data.data.id,
      permissions: me.Data.data.roles || [],
      userRole: me.Data.data.roles?.[0]?.name || '',
      emailVerified: me.Data.data.email_verified_at,
      companyId: me.Data.data.myCompany?.id,
      systemPermissions: []
    }

    try {
      const perms = await CommonService.getAllSystemPageAccess()
      if (perms?.Success) storeData.systemPermissions = perms.Data
    } catch (_) {}

    this.setUserStoreData(storeData)

    router.push('/my-profile')
    return { success: true }
  }

  async logout() {
    try {
      if (this.getJwt()) {
        await ApiService.post('/logout')
      }
    } catch (_) {
      // ignore
    } finally {
      this.clearAuthData()
      router.push('/login')
    }
  }

  clearAuthData() {
    this.removeJwt()
    this.removeJWTExpiry()
    this.removeUserStoreData()
    this.setUserData(null)

    localStorage.removeItem('impersonation_active')
    localStorage.removeItem('impersonated_user')
    localStorage.removeItem('original_user')
  }

  async forgotPassword(email) {
    try {
      const response = await ApiService.post('/forgot-password', {}, { email })

      if (response?.Success) {
        return {
          success: true,
          message: response.Data?.message || 'Password reset link sent successfully'
        }
      }

      return {
        success: false,
        message: response?.Data?.message || 'Failed to send reset link'
      }
    } catch (error) {
      console.error('Forgot password error:', error)

      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to send reset link'
      }
    }
  }

  async resetPassword(resetData) {
    try {
      const response = await ApiService.post('/reset-password', {}, resetData)

      if (response?.Success) {
        return {
          success: true,
          message: response.Data?.message || 'Password reset successfully'
        }
      }

      return {
        success: false,
        message: response?.Data?.message || 'Failed to reset password'
      }
    } catch (error) {
      console.error('Reset password error:', error)

      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to reset password'
      }
    }
  }

  async changePassword(passwordData) {
    try {
      const response = await ApiService.post('/change-password', {}, passwordData)

      if (response?.Success) {
        return {
          success: true,
          message: response.Data?.message || 'Password changed successfully'
        }
      }

      return {
        success: false,
        message: response?.Data?.message || 'Failed to change password'
      }
    } catch (error) {
      console.error('Change password error:', error)

      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to change password'
      }
    }
  }



  // ==========================================
  // TOKEN REFRESH
  // ==========================================
  async refreshToken() {
    try {
      const response = await ApiService.post('/refresh')
      if (response?.Success) {
        this.setJwt(response.Data.access_token)
        this.setJWTExpiry(response.Data.expires_at)
        return { success: true }
      }
    } catch (_) {}

    this.logout()
    return { success: false }
  }

  async checkAndRefreshToken() {
    if (this.isImpersonating()) return { success: true }

    const expiry = this.getJWTExpiry()
    if (!expiry) return { success: false }

    const remaining = new Date(expiry).getTime() - Date.now()

    if (remaining < 5 * 60 * 1000) {
      return this.refreshToken()
    }

    return { success: true }
  }

  // ==========================================
  // IMPERSONATION
  // ==========================================
  getImpersonationStatus() {
    const active = localStorage.getItem('impersonation_active') === 'true'

    if (!active) {
      return {
        isImpersonating: false,
        originalUser: null,
        impersonatedUser: null
      }
    }

    try {
      return {
        isImpersonating: true,
        originalUser: JSON.parse(localStorage.getItem('original_user')),
        impersonatedUser: JSON.parse(localStorage.getItem('impersonated_user'))
      }
    } catch (e) {
      console.error('Error parsing impersonation data', e)
      return {
        isImpersonating: false,
        originalUser: null,
        impersonatedUser: null
      }
    }
  }

  isImpersonating() {
    return localStorage.getItem('impersonation_active') === 'true'
  }
}

export default new AuthenticationService()
